import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Container, Row, Col, Form, Button, Alert} from 'react-bootstrap';
import {
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaClock,
    FaFacebook,
    FaInstagram,
    FaLinkedin,
    FaHome
} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import config from '../../../config/config';
import {sendNewsletterSubscriptionEmail, NewsletterSubscriptionData} from '../../../services/newsletterService';
import './Footer.css';
import logo from "../../../assets/images/logo.webp";

const Footer: React.FC = () => {
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();

    // Determine direction class based on current language
    const directionClass = i18n.dir ? i18n.dir() === 'rtl' ? 'rtl' : 'ltr' : 'ltr';

    const handleNewsletterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newsletterEmail.trim()) {
            const subscriptionData: NewsletterSubscriptionData = {email: newsletterEmail};
            const isSuccessful = await sendNewsletterSubscriptionEmail(subscriptionData);
            if (isSuccessful) {
                setIsSubscribed(true);
                setNewsletterEmail('');
                setTimeout(() => setIsSubscribed(false), 3000);
            } else {
                // Handle error - you might want to show an error message
                console.error('Newsletter subscription failed');
            }
        }
    };

    const handleLinkClick = (path: string) => {
        window.scrollTo(0, 0);
        navigate(path);
    };

    return (
        <footer className={`footer-container ${directionClass}`}>
            <Container fluid className="footer-main py-5">
                <Container>
                    <Row className="footer-sections">
                        <Col lg={3} md={3} className="mb-3 footer-brand-section">
                            <div className="footer-logo" style={{direction: 'ltr', display: 'inline-block'}}>
                                <Link to="/" onClick={() => handleLinkClick('/')}>
                                    <img src={logo} alt="A2S SARL Logo"/>
                                </Link>

                            </div>
                            <p className="footer-slogan"
                               style={{textAlign: 'justify', maxWidth: '300px'}}>{t('footer.about.description')}</p>


                            <div className="social-links">
                                <a href={config.contact.facebook} target="_blank" rel="noopener noreferrer"
                                   aria-label="Facebook"
                                   className="social-icon"><FaFacebook/></a>
                                <a href={config.contact.instagram} target="_blank" rel="noopener noreferrer"
                                   aria-label="Instagram"
                                   className="social-icon"><FaInstagram/></a>
                                <a href={config.contact.linkedin} target="_blank" rel="noopener noreferrer"
                                   aria-label="linkedin"
                                   className="social-icon"><FaLinkedin/></a>
                            </div>
                        </Col>
                        <Col lg={2} md={2} className="mb-2 footer-links-section">
                            <h3 className="footer-title">{t('footer.quickLinks.title')}</h3>
                            <div className="footer-links">
                                <a href="/" onClick={() => handleLinkClick('/')}
                                   className="footer-link">{t('footer.quickLinks.home')}</a>
                                <a href="/about" onClick={() => handleLinkClick('/about')}
                                   className="footer-link">{t('footer.quickLinks.about')}</a>
                                <a href="/services" onClick={() => handleLinkClick('/services')}
                                   className="footer-link">{t('footer.quickLinks.services')}</a>
                                <a href="/projects" onClick={() => handleLinkClick('/projects')}
                                   className="footer-link">{t('footer.quickLinks.projects')}</a>
                                <a href="/boutique" onClick={() => handleLinkClick('/boutique')}
                                   className="footer-link">{t('nav.shop', 'Boutique')}</a>
                                <a href="/blog" onClick={() => handleLinkClick('/blog')}
                                   className="footer-link">{t('footer.quickLinks.blog')}</a>
                                <a href="/contact" onClick={() => handleLinkClick('/contact')}
                                   className="footer-link">{t('footer.quickLinks.contact')}</a>
                            </div>
                        </Col>
                        <Col lg={4} md={4} className="mb-4 col-4 footer-contact-section">
                            <h3 className="footer-title">{t('footer.contact.title')}</h3>
                            <div className="contact-info-footer">
                                <div className="contact-item">
                                    <FaMapMarkerAlt className="contact-item-icon"/>
                                    <span className="contact-address" style={{
                                        textAlign: 'justify',
                                        maxWidth: '200px'
                                    }}>{t('footer.contact.address')} </span>
                                </div>
                                <div className="contact-item">
                                    <FaPhone className="contact-item-icon"/>
                                    <span style={{
                                        direction: 'ltr',
                                        display: 'inline-block'
                                    }}>{config.contact.phone} / {config.contact.phone2}</span>
                                </div>

                                <div className="contact-item">

                                    <FaHome className="contact-item-icon"/>
                                    <span style={{
                                        direction: 'ltr',
                                        display: 'inline-block'
                                    }}> {config.contact.phoneFix}</span>
                                </div>


                                <div className="contact-item">
                                    <FaEnvelope className="contact-item-icon"/>
                                    <span><a href={`mailto:${config.contact.email}`}
                                             className="styled-a">{config.contact.email}</a></span>
                                </div>
                                <div className="contact-item">
                                    <FaClock className="contact-item-icon"/>
                                    <span>{t('footer.contact.workHours', 'Lun - Ven: 8h00 - 18h00')}</span>
                                </div>
                            </div>
                        </Col>
                        <Col lg={3} md={3} className="mb-3 footer-newsletter-section">
                            <h3 className="footer-title">{t('footer.newsletter.title')}</h3>
                            <p className="newsletter-description">{t('footer.newsletter.description')}</p>
                            {isSubscribed && <Alert variant="success">{t('footer.newsletter.success')}</Alert>}
                            <Form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                                <Form.Group className="newsletter-group">
                                    <Form.Control
                                        type="email"
                                        placeholder={t('footer.newsletter.placeholder')}
                                        value={newsletterEmail}
                                        onChange={(e) => setNewsletterEmail(e.target.value)}
                                        className="newsletter-input"
                                        required
                                    />
                                    <Button type="submit"
                                            className="newsletter-button">{t('footer.newsletter.button')}</Button>
                                </Form.Group>
                            </Form>
                        </Col>
                    </Row>
                </Container>
            </Container>

            <div className="footer-bottom">
                <Container>
                    <Row className="footer-bottom-row">
                        <Col className="text-center">
                            <p className="footer-copyright">{new Date().getFullYear()} - {t('footer.copyright')} &nbsp;|&nbsp;
                                <span style={{cursor: 'pointer'}}
                                        onClick={() => handleLinkClick('/privacy-policy')}>{t('footer.privacy')}</span>
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
        </footer>
    );
};

export default Footer;
